import { Collapse, Divider, Grid2 as Grid, LinearProgress, Stack, TextField } from "@mui/material"
import { useEffect } from "react"
import { useFindAcquisitionModes, useFindAcquisitionModesAoiSpecs } from "../../api/AcquisitionModeApi"
import { useFetchProjectFullData } from "../../api/ProjectsApi"
import { NexSelect } from "../../components/Fields/NexSelect"
import { ModeTabs } from "../../components/Modes/ModeTabs"
import { Label, PrimaryLabel } from "../../components/Typography/Typography"
import { useNexusProjectNumber } from "../../hooks/useNexusParams"
import { AoiType, aoiOptions, isAoiTypeCorridor } from "../../models/aois/AoiName"
import { useProjectEditStore } from "../../zstore/ProjectEditStore"
import { AOICalculations } from "./AOICalculations"
import { AOISelect } from "./AOISelect"
import { AOIUpload } from "./AOIUpload"
import { useProjectEditorStyles } from "./ProjectEditorStyles"

export const AOITab = () => {
    const { classes, cx } = useProjectEditorStyles()

    const projectNumber = useNexusProjectNumber()
    const aoi = useProjectEditStore((state) => state.selectedAoi)
    const updateAoiField = useProjectEditStore((state) => state.updateAoiField)
    const setError = useProjectEditStore((state) => state.setError)

    // Just checking if it is still loading
    const projectResponse = useFetchProjectFullData(projectNumber)
    const acquisitionModesResponse = useFindAcquisitionModes()
    const aoiSpecsResponse = useFindAcquisitionModesAoiSpecs()

    // This should already be loaded and the StepperForm should show its loading bar, so this loading message should not
    // be able to show
    const loading = acquisitionModesResponse.isPending || aoiSpecsResponse.isPending || projectResponse.isPending

    useEffect(() => {
        if (aoiSpecsResponse.error?.message != null) {
            setError(aoiSpecsResponse.error.message)
        }
    }, [aoiSpecsResponse.error?.message])

    const handleAoiTypeChange = async (type?: AoiType) => {
        const isCorridor = isAoiTypeCorridor(type)

        // clear corridor value if other type besides corridor is selected
        if (!isCorridor && aoi?.corridor_length_m != null) {
            await updateAoiField("corridor_length_m", undefined)
        }

        await updateAoiField("aoi_type", type)
    }

    if (loading) {
        return <LinearProgress sx={{ mb: 2, mt: 2 }} />
    }

    return (
        <Stack>
            <AOISelect />
            <Divider sx={{ mt: 2 }} />

            <Collapse in={aoi != null} timeout={{ enter: 800, exit: 400 }}>
                <Stack spacing={2}>
                    <PrimaryLabel>AOI Attributes</PrimaryLabel>

                    <TextField
                        label="AOI Name"
                        variant="outlined"
                        helperText="Required"
                        value={aoi?.name ?? ""}
                        onChange={(event) => updateAoiField("name", event.target.value)}
                        className={classes.input__md}
                    />

                    <AOIUpload />

                    <Label gutterBottom="sm">AOI Type</Label>
                    <NexSelect
                        width="md"
                        helperText="Required"
                        value={aoi?.aoi_type ?? ""}
                        options={aoiOptions}
                        onChange={(event) => handleAoiTypeChange(event.target.value as AoiType)}
                    />

                    <AOICalculations />

                    <Grid sx={{ pt: 2 }}>
                        <ModeTabs
                            modeSelectionDisabled={projectResponse.isPending}
                            modes={acquisitionModesResponse.data!}
                            aoiSpecs={aoiSpecsResponse.data!}
                        />
                    </Grid>

                    <Label gutterBottom="sm">AOI Notes</Label>
                    <TextField
                        multiline
                        minRows={4}
                        fullWidth
                        variant="outlined"
                        helperText="Required"
                        value={aoi?.notes ?? ""}
                        onChange={(event) => updateAoiField("notes", event.target.value)}
                        className={cx(classes.gutterBottom__lg)}
                    />
                </Stack>
            </Collapse>
        </Stack>
    )
}
